import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Text,
  TextInput,
  Tabs,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import i18n from "src/i18n";
import { z } from "zod";

import { getLabCategoryValues } from "@data/dropdownValues";

import { useUpdateClinicLaboratoryService } from "@hooks/mutations/clinic/useUpdateClinicLaboratoryService";
import { useGetClinicCollectionMethodsOptions } from "@hooks/queries/clinic/useGetClinicCollectionMethodsOptions";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";

import { ClinicLaboratoryService } from "@customTypes/clinic";

import { StyledEditLaboratoryServiceModalContent } from "./EditLaboratoryServiceModal.styled";
import { CustomFormBuilder } from "@components/CustomFormBuilder";


const editFormSchema = () =>
  z.object({
    type: z.string().min(1, i18n.t("name-required")),
    category: z.string().optional(),
    price: z.number().min(1, i18n.t("price-required")),
    collectionMethod: z.string().optional(),
    collectionPrice: z.number().min(0).optional(),
    partner_id: z.string().optional(),
  });

type EditFormInputs = z.infer<ReturnType<typeof editFormSchema>>;

interface EditLaboratoryServiceModalProps {
  opened: boolean;
  close: () => void;
  laboratoryService: ClinicLaboratoryService | null;
}



export const EditAnalysisServiceModal = ({
  opened,
  close,
  laboratoryService,
}: EditLaboratoryServiceModalProps) => {
  const { t } = useTranslation();

  const {
    mutate: updateLaboratoryService,
    isLoading: isUpdateLaboratoryServiceLoading,
  } = useUpdateClinicLaboratoryService();

  const {
    data: collectionMethodsData,
    isLoading: isCollectionMethodsLoading,
  } = useGetClinicCollectionMethodsOptions();

  const { data: partnersData, isLoading: isPartnersLoading } = useGetPartners({ isActive: true });

  const partnersOptions = useMemo(() => {
    return (partnersData || []).map(partner => ({
      label: partner.name,
      value: partner._id,
    }));
  }, [partnersData]);

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(editFormSchema()),
    initialValues: {
      type: "",
      category: "",
      price: 0,
      collectionMethod: "",
      collectionPrice: 0,
      partner_id: "",
    },
  });

  // Update form values when laboratoryService changes
  useEffect(() => {
    if (laboratoryService && opened) {
      // Convert collection method name to ID for the Select component
      let collectionMethodId = laboratoryService.collectionMethod || "";
      if (laboratoryService.collectionMethod && collectionMethodsData) {
        const selectedMethod = collectionMethodsData.find(
          method => method.label === laboratoryService.collectionMethod
        );
        collectionMethodId = selectedMethod?.value || laboratoryService.collectionMethod;
      }

      form.setValues({
        type: laboratoryService.type || "",
        category: laboratoryService.category || "",
        price: laboratoryService.price || 0,
        collectionMethod: collectionMethodId,
        collectionPrice: laboratoryService.collectionPrice || 0,
        partner_id: laboratoryService.partner_id || "",
      });
    }
  }, [laboratoryService, opened, collectionMethodsData]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!opened) {
      form.reset();
    }
  }, [opened]);

  const handleFormSubmit = (values: EditFormInputs) => {
    if (!laboratoryService?.id) return;

    // Convert collection method ID to name
    let collectionMethodName = values.collectionMethod;
    if (values.collectionMethod && collectionMethodsData) {
      const selectedMethod = collectionMethodsData.find(
        method => method.value === values.collectionMethod
      );
      collectionMethodName = selectedMethod?.label || values.collectionMethod;
    }

    updateLaboratoryService(
      {
        laboratoryServiceId: laboratoryService.id,
        laboratoryService: {
          ...values,
          collectionMethod: collectionMethodName,
        },
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("edit-laboratory-service")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="lg">
      <Tabs defaultValue="details">
        <Tabs.List>
          <Tabs.Tab value="details">{t("details")}</Tabs.Tab>
          <Tabs.Tab value="form">{t("pre-appointment-form")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details">
          <StyledEditLaboratoryServiceModalContent>
            <form
              className="form"
              onSubmit={form.onSubmit(
                values => handleFormSubmit(values),
                errors => {
                  const firstErrorPath = Object.keys(errors)[0];
                  form.getInputNode(firstErrorPath)?.focus();
                }
              )}>
              <div className="form-field">
                <TextInput
                  label={t("type")}
                  withAsterisk
                  {...form.getInputProps("type")}
                  placeholder={t("eg", { example: "Blood Test" })}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("partner")}
                  placeholder={isPartnersLoading ? t("loading") : t("select-partner")}
                  data={partnersOptions}
                  searchable
                  clearable
                  disabled={isPartnersLoading}
                  {...form.getInputProps("partner_id")}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("category")}
                  placeholder={t("select-category")}
                  data={getLabCategoryValues()}
                  {...form.getInputProps("category")}
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("price")}
                  withAsterisk
                  {...form.getInputProps("price")}
                  min={0}
                  step={1}
                  placeholder="5000"
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("collection-method")}
                  placeholder={t("select-a-collection-method")}
                  data={[
                    { label: "-", value: "" },
                    ...(collectionMethodsData || [])
                  ]}
                  {...form.getInputProps("collectionMethod")}
                  disabled={isCollectionMethodsLoading}
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("collection-price")}
                  {...form.getInputProps("collectionPrice")}
                  min={0}
                  step={1}
                  placeholder="500"
                />
              </div>

              <div className="form-actions">
                {/* <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={isUpdateLaboratoryServiceLoading}>
                  {t("cancel")}
                </Button> */}
                <PermissionGate permission="resources.edit"><Button
                  type="submit"
                  loading={isUpdateLaboratoryServiceLoading}
                  fullWidth>
                  {t("update")}
                </Button></PermissionGate>
              </div>
            </form>
          </StyledEditLaboratoryServiceModalContent>
        </Tabs.Panel>

        <Tabs.Panel value="form">
          <div style={{ padding: "0 20px 20px" }}>
            {laboratoryService?.id && (
              <CustomFormBuilder serviceId={laboratoryService.id} />
            )}
          </div>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
};
